import isPermissionEnabled from "common/isPermissionEnabled"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadPDF from "components/Shared/DownloadPDF"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import Switch from "components/Shared/Switch"
import TableShimmer from "components/Shared/TableShimmer"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import formatDateTime from "components/Shared/formatDateTime"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import {
  Badge,
  Card,
  CardBody,
  Col,
  Row,
  Table,
} from "reactstrap"
import { exportData } from "store/actions/commonAction"
import {
  deleteCoupon,
  getAllCoupons,
  getAllCouponsGroupsList,
  getAllPromotionsList,
  updateCouponStatus,
} from "store/actions/promotionsAction"
import { showAlert } from "store/reducers/alertReducer"
import ConfirmModal from "components/Common/ConfirmModal"

const Coupons = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Coupons List ")
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const { couponGroupId } = useParams()

  const navigate = useNavigate()

  const {
    loading,
    couponsItemsPerPage: itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    isUpdating,
    isLoading,
    coupons,
    couponsGroupsList,
    promotionsList,
    couponLoading,
  } = useSelector(state => state.Promotions)

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")

  const [selectedId, setSelectedId] = useState(null)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const couponGroupsParam = searchParams.get("couponGroups")
  const typeParam = searchParams.get("type")
  const promotionParam = searchParams.get("promotion")

  const { userRole, accountType } = useSelector(state => state.Login)
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "2"),
    // Empty string = no type filter ("Select Coupon Type")
    type: typeParam || "",
    couponExpiry: searchParams.get("couponExpiry") || "ALL",
    couponGroups: couponGroupsParam
      ? couponGroupsParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    promotion: promotionParam
      ? promotionParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
  })
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [statusCouponId, setStatusCouponId] = useState(null)
  const [statusToUpdate, setStatusToUpdate] = useState(null)

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      14,
      "promotion_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      14,
      "promotion_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      14,
      "promotion_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      14,
      "promotion_4",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  useEffect(() => {
    fetchAllCoupons()
    couponsGroupsList.length === 0 && fetchCouponsGroupsList()
    promotionsList.length === 0 && fetchPromotionsList()
  }, [mode])

  const couponGroup = mode === "allCoupons" ? [] : [couponGroupId]

  const fetchAllCoupons = () => {
    const { status, sortOrder, type, couponGroups, promotion, couponExpiry } =
      selectedFilters
    const updatedType = type ? [type] : []

    const updatedPromotion = promotion?.map(item => item.value) || []

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      couponGroup,
      type: updatedType,
      promotion: updatedPromotion,
      keyword: searchTerm,
      couponExpiry: couponExpiry, //NOT-EXPIRED, EXPIRED,ALL
    }
    dispatch(getAllCoupons(payload))
    setIsApiCalled(true)
  }

  const fetchCouponsGroupsList = () => {
    dispatch(getAllCouponsGroupsList())
  }

  const fetchPromotionsList = () => {
    dispatch(getAllPromotionsList())
  }

  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Active"), value: 1 },
        { label: t("Deactive"), value: 0 },
      ],
      placeholder: t("All"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
    ...(mode === "allCoupons"
      ? [
          {
            name: "couponExpiry",
            type: "select",
            options: [
              { label: t("All"), value: "ALL" },
              { label: t("Live"), value: "ACTIVE" },
              { label: t("Upcoming"), value: "UPCOMING" },
              { label: t("Expired"), value: "EXPIRED" },
            ],
            placeholder: t("All"),
          },
          {
            name: "type",
            type: "select",
            options: [
              { label: t("Select Coupon Type"), value: "" },
              { label: t("Group"), value: "group" },
              { label: t("Public"), value: "public" },
              { label: t("Private"), value: "private" },
            ],
            placeholder: t("Select Coupon Type"),
          },
        ]
      : []),
  ]

  if (mode === "allCoupons") {
    filters.push({
      name: "promotion",
      type: "multiSelect",
      options: [
        ...promotionsList.map(item => ({
          label: item.name,
          value: item._id,
        })),
      ],
      placeholder: t("Discount"),
    })
  }

  if (mode === "allCoupons") {
    filters.push({
      name: "couponGroups",
      type: "multiSelect",
      options: [
        ...couponsGroupsList.map(item => ({
          label: language === "sa" ? item.nameAr : item.name,
          value: item._id,
        })),
      ],
      placeholder: t("Coupon Groups"),
    })
  }

  // if (mode === "allCoupons") {
  //   filters.push({
  //     name: "couponExpiry",
  //     type: "select",
  //     options: [
  //       { label: t("All"), value: "ALL" },
  //       { label: t("Live"), value: "ACTIVE" },
  //       { label: t("Upcoming"), value: "UPCOMING" },
  //       { label: t("Expired"), value: "EXPIRED" },
  //     ],
  //     placeholder: t("All"),
  //   })
  // }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const getApiCall = (page = 1) => {
    const { status, sortOrder, type, couponGroups, promotion, couponExpiry } =
      selectedFilters
    const updatedType = type ? [type] : []

    const updatedCouponGroups =
      mode === "allCoupons"
        ? couponGroups?.map(item => item.value)
        : [couponGroupId]

    const updatedPromotion = promotion?.map(item => item.value) || []

    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page)
    couponExpiry && searchParams.set("couponExpiry", couponExpiry.toString())

    if (couponGroups.length > 0) {
      const couponGroupsString = couponGroups
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("couponGroups", couponGroupsString)
    }
    if (type) {
      searchParams.set("type", String(type))
    }
    if (promotion?.length > 0) {
      const promotionString = promotion
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("promotion", promotionString)
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status),
      orderBy: Number(sortOrder),
      couponGroup: updatedCouponGroups,
      promotion: updatedPromotion,
      type: updatedType,
      keyword: searchTerm || "",
      couponExpiry: couponExpiry,
    }
    dispatch(getAllCoupons(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          mode === "allCoupons"
            ? navigate(`/coupons?${searchParams.toString()}`)
            : navigate(
                `/coupons-group/${couponGroupId}/coupons?${searchParams.toString()}`
              )
        }
      })
      .catch(error => {})
  }

  const handleSearch = () => {
    getApiCall(1) // Always start from page 1 for search
  }

  const onPageChange = page => {
    getApiCall(page) // Use the selected page for pagination
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     const delayDebounceFn = setTimeout(() => {
  //       handleSearch()
  //     }, 500)

  //     return () => clearTimeout(delayDebounceFn)
  //   }
  // }, [searchTerm])

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters])

  const handleReset = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 2,
      keyword: "",
      couponGroup,
      type: [],
      couponExpiry: "ALL",
    }
    dispatch(getAllCoupons(payload))

    setSelectedFilters({
      status: 2,
      sortOrder: 2,
      type: "",
      couponExpiry: "ALL",
      couponGroups: [],
      promotion: [],
    })
    setSearchTerm("")
    mode === "allCoupons"
      ? navigate(`/coupons?page=${1}`)
      : navigate(`/coupons-group/${couponGroupId}/coupons?page=${1}`)
  }

  const handleConfirmStatusUpdate = () => {
    if (!statusCouponId) return
    setSelectedId(statusCouponId)
    const payload = {
      id: statusCouponId,
      status: statusToUpdate,
    }
    dispatch(updateCouponStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
      .finally(() => {
        setShowStatusModal(false)
        setStatusCouponId(null)
        setStatusToUpdate(null)
      })
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // 2 FOR DELETE
    }
    dispatch(deleteCoupon(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
          setItemIdToDelete(null)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleDownloadPDF = () => {
    const { status, sortOrder, type, couponGroups, couponExpiry, promotion } =
      selectedFilters

    const updatedType = type ? [type] : []

    const updatedCouponGroups =
      mode === "allCoupons"
        ? couponGroups?.map(item => item.value)
        : [couponGroupId]

    const updatedPromotion = promotion?.map(item => item.value) || []

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "COUPON",
      exportType: "pdf",
      couponType: updatedType,
      couponGroup: updatedCouponGroups,
      promotion: updatedPromotion,
      couponExpiry: couponExpiry,
    }

    dispatch(exportData(payload))
      .then(action => {
        const blobData = action.payload
        DownloadPDF(blobData, t("Coupons"))
      })
      .catch(error => {})
  }

  const exportToCSV = async () => {
    const { status, sortOrder, type, couponGroups, promotion, couponExpiry } =
      selectedFilters

    const updatedType = type ? [type] : []

    const updatedCouponGroups =
      mode === "allCoupons"
        ? couponGroups?.map(item => item.value)
        : [couponGroupId]

    const updatedPromotion = promotion?.map(item => item.value) || []

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "COUPON",
      exportType: "csv",
      couponType: updatedType,
      couponGroup: updatedCouponGroups,
      promotion: updatedPromotion,
      couponExpiry: couponExpiry,
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Coupons"))
    }
  }

  const handleExcelDownload = async () => {
    const { status, couponGroups, couponExpiry, promotion } = selectedFilters

    const updatedCouponGroups =
      mode === "allCoupons"
        ? couponGroups?.map(item => item.value)
        : [couponGroupId]

    const updatedPromotion = promotion?.map(item => item.value) || []

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "COUPON",
      exportType: "xlsx",
      couponType: [],
      couponGroup: updatedCouponGroups,
      promotion: updatedPromotion,
      couponExpiry: couponExpiry,
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Coupons"))
    }
  }

  const getCouponActionItems = item => [
    ...(usersPermissions.edit
      ? [
          {
            key: "edit",
            content: (
              <>
                <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                {t("Edit")}
              </>
            ),
            onClick: () => navigate(`/coupons/edit?id=${item._id}`),
          },
        ]
      : []),
    {
      key: "view",
      content: (
        <>
          <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
          {t("View")}
        </>
      ),
      onClick: () => navigate(`/coupons/coupon-details?id=${item._id}`),
    },
    ...(usersPermissions.delete
      ? [
          {
            key: "delete",
            content: (
              <>
                <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                {t("Delete")}
              </>
            ),
            onClick: () => {
              setItemIdToDelete(item._id)
              setShowDeleteModal(true)
            },
          },
        ]
      : []),
  ]

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Coupons List")}
                isAddBtn={usersPermissions.create && mode === "allCoupons"}
                addBtnText={t("Add Coupon")}
                onAddBtnClick={() => navigate(`/coupons/add`)}
                isExportData={couponLoading ? false : usersPermissions.create}
                onDownloadPDF={handleDownloadPDF}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
              />
              <CardBody>
                {/* {!loading && ( */}
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Enter coupon code or name")}
                  colSize={3}
                />
                <div className="table-responsive">
                  {couponLoading ? (
                    <TableShimmer row={7} col={7} />
                  ) : coupons.length === 0 ? (
                    <p className="text-center my-2">{t("No coupons found")}</p>
                  ) : (
                    <Table className="table table-nowrap mb-0 capitalize_td" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Coupon Code")}</th>
                          <th>{t("Starts At")}</th>
                          <th>{t("Expires At")}</th>
                          <th>{t("Usage Count")}</th>
                          <th>{t("Last Update By")}</th>
                          <th>{t("Created By")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Action")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coupons.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() =>
                              navigate(`/coupons/coupon-details?id=${item._id}`)
                            }
                            className="cursor-pointer"
                          >
                            <th scope="row">
                              {(currentPage - 1) * 10 + index + 1}
                            </th>
                            <td>
                              {language === "sa" ? item.nameAr : item.name}
                            </td>
                            <td>{item.code || "-"}</td>
                            <td>{formatDateTime(item.startsAt)}</td>
                            <td>{formatDateTime(item.expiresAt)}</td>
                            <td>
                              {item?.usageCount ? (
                                <span
                                  className="text-decoration-underline text-primary"
                                  onClick={e => {
                                    e.stopPropagation()
                                    navigate(`/coupon-usage/${item._id}`)
                                  }}
                                >
                                  {item?.usageCount}
                                </span>
                              ) : (
                                item?.usageCount
                              )}
                            </td>
                            <td>
                              {item?.lastUpdatedBy
                                ? `${item?.lastUpdatedBy?.firstName || ""} ${
                                    item?.lastUpdatedBy?.lastName || ""
                                  }`.trim()
                                : "-"}
                            </td>
                            <td>
                              {item?.createdBy
                                ? `${item?.createdBy?.firstName || ""} ${
                                    item?.createdBy?.lastName || ""
                                  }`.trim()
                                : ""}
                            </td>
                            <td>
                              {item.status === 1 ? (
                                <Badge className="bg-success">
                                  {t("Active")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Deactive")}
                                </Badge>
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                {usersPermissions.edit && (
                                  <Switch
                                    onClickHandler={e => {
                                      e.stopPropagation()
                                      setStatusCouponId(item._id)
                                      setStatusToUpdate(
                                        item.status === 1 ? "0" : "1"
                                      ) // toggle status
                                      setShowStatusModal(true)
                                    }}
                                    isChecked={item.status === 1}
                                    disabled={
                                      item._id === selectedId && isUpdating
                                    }
                                  />
                                )}
                                <TableActionsDropdown items={getCouponActionItems(item)} />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>

                {!couponLoading && (
                  <div className="mt-3">
                    <Paginations
                      data={coupons}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Coupon"),
        })}
      />
      <ConfirmModal
        show={showStatusModal}
        isLoading={isUpdating}
        onDeleteClick={handleConfirmStatusUpdate}
        onCloseClick={() => setShowStatusModal(false)}
        buttonText={t("Yes")}
        bodyTitle={t("Are you sure you want to update the status?")}
      />
    </div>
  )
}

export default Coupons
